import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { decodeToken } from "../utils/tokenUtils";
import { logger } from "../utils/logger";
import { formatFindingName } from "../utils/textFormatters";
import { detectedFindings } from "../utils/inferenceStatus";
import { downloadCombinedReports, summarizeExportShortfall } from "../utils/reportDownload";
import { makeDownloadableReport } from "../utils/reportDocument";
import { fetchReport } from "../hooks/useReportQuery";
import {
  useReaderCohort,
  useStudyBbox,
  fetchReportState,
  selectGalleryFindings,
  type ReaderStudyItem,
} from "../hooks/useStudyQueries";
import { useFindingSlices } from "../hooks/useFindingSlices";
import type { TileState } from "./PreviewGalleryView";
import {
  parseStudy,
  buildMultipartBody,
  makeBoundary,
  multipartContentType,
  DEFAULT_STRIP_PATIENT_INFO,
  StudyValidationError,
  type ParsedStudy,
} from "../utils/dicomUpload";
import {
  UploadPageView,
  UploadPipelineBox,
  DropzonePanel,
  ParsingPanel,
  PhiReviewPanel,
  UploadProgressPanel,
  ProcessingPipeline,
  UploadDetailPanel,
  ErrorPanel,
  UploadModeChooser,
  BatchReviewPanel,
  BatchUploadPanel,
  BatchAnalyzePanel,
  BatchReviewSummary,
  BatchProgressStats,
} from "./upload";
import { useBatchUploadContext } from "../context/BatchUploadContext";
import { UPLOAD_MAX_MB, UPLOAD_SINGLE_REQUEST_MB } from "../config/uploadLimits";
import { uploadReceiptCohortId } from "../utils/uploadReceipt";
import {
  DirectUploadError,
  isDirectUploadCancelled,
  uploadStudyDirect,
} from "../utils/directStudyUpload";
import type {
  UploadPhase,
  PhiFinding,
  StudyValidation,
  UploadListItem,
  ProcessingStage,
} from "./upload/types";

/**
 * Live Upload — in-browser study upload (Slice LU-A), worklist-shaped redesign.
 * Design lane (LU-A10) owns the presentational layer in `./upload/*`; this page
 * owns the phase machine, queries, and handlers (logic lane, LU-A2…A9).
 *
 * Design + tracker: NON_REPOS/plans/2026-06-09-live-upload-design.md
 * Composition guide: ./upload/index.ts · frozen contract: design doc §12
 */
export type { UploadPhase, PhiFinding, StudyValidation } from "./upload/types";

// A whole-study upload legitimately runs for minutes (a large study, a slow
// uplink, and the synchronous server-side STOW + dispatch). The global axios
// default (AuthContext: 30s) would abort a still-succeeding upload and falsely
// report failure — which also tempts the user into a duplicate re-upload. Use a
// generous ceiling matching the backend's device-server forward timeout
// (httpx 600s) rather than inheriting the 30s default.
const UPLOAD_REQUEST_TIMEOUT_MS = 10 * 60 * 1000;

// ── cohort study → list item / detail helpers ──────────────────────────────

function complexityFromCount(n: number): 1 | 2 | 3 | 4 {
  if (n >= 6) return 4;
  if (n >= 3) return 3;
  if (n >= 1) return 2;
  return 1;
}

function mapToListItem(study: ReaderStudyItem): UploadListItem {
  const status: UploadListItem["status"] =
    study.status === "completed"
      ? "ready"
      : study.status === "failed" || study.status === "cancelled"
        ? "failed"
        : "processing";
  const found = detectedFindings(study).map(formatFindingName);
  return {
    id: study.study_uid,
    // device-server studies resolve in the viewer by job_id — bare study_uid 404s.
    viewerId: study.job_id ? `${study.study_uid}::${study.job_id}` : study.study_uid,
    status,
    leadFinding: found[0],
    supportingText: found.slice(1).join(" · ") || undefined,
    findingCount: found.length,
    complexity: complexityFromCount(found.length),
    modality: "CT",
  };
}

function metaLine(u: UploadListItem): string {
  const parts = [u.modality, u.studyDescription].filter(Boolean) as string[];
  if (u.seriesCount != null) parts.push(`${u.seriesCount} series`);
  else if (u.metaOverride) parts.push(u.metaOverride);
  return parts.join(" · ");
}

function uploadErrorMessage(err: unknown): string {
  if (err instanceof DirectUploadError) {
    if (err.code === "ECONNABORTED") {
      return "The upload timed out before it finished. Please try again.";
    }
    if (err.status === 409) return "The receiver rejected every instance in this study.";
    if (err.status === 413) return "This study is larger than the upload limit.";
    if (err.status === 401) return "Upload authorization expired. Please retry the study.";
    if (err.status === 400)
      return "The receiver could not validate this upload. Check the study and try again.";
    if (err.status != null && err.status >= 500) {
      const suffix = err.detail ? ` (${err.detail})` : "";
      return `The upload service hit a problem and stopped${suffix}. This isn't your study - please retry shortly, and contact support if it persists.`;
    }
    return "The upload couldn't be completed. Please try again.";
  }
  if (axios.isAxiosError(err)) {
    // Client-side timeout/abort: the request never got a response.
    if (err.code === "ECONNABORTED" || /timeout/i.test(err.message)) {
      return "The upload timed out before it finished. Please try again.";
    }
    const status = err.response?.status;
    if (status === 409) return "The receiver rejected every instance in this study.";
    if (status === 413) return "This study is larger than the upload limit.";
    if (status === 401) return "Your session expired — sign in again and retry.";
    if (status === 400)
      return "This doesn't look like a valid CT study. Check the files and try again.";
    if (status != null && status >= 500) {
      // Server/receiver-side failure — re-uploading the SAME study won't help,
      // so say it's not the user's study (avoids pointless duplicate uploads).
      // The backend detail (e.g. "Upload receiver error (403)") is benign and
      // makes the failure diagnosable from the UI.
      const data = err.response?.data;
      const detail =
        data && typeof data === "object" ? (data as { detail?: unknown }).detail : undefined;
      const suffix = typeof detail === "string" ? ` (${detail})` : "";
      return `The upload service hit a problem and stopped${suffix}. This isn't your study — please retry shortly, and contact support if it persists.`;
    }
    return "The upload couldn't be completed. Please try again.";
  }
  return "The upload couldn't be completed. Please try again.";
}

/**
 * Detail pane for a finished upload — reuses the worklist gallery machinery
 * (`useStudyBbox` → `selectGalleryFindings` → `useFindingSlices`). Its own
 * component so the hooks run unconditionally only while a ready study is open.
 */
const ReadyUploadDetail: React.FC<{
  study: UploadListItem;
  detected: string[];
  meta: string;
  justProcessed: boolean;
  onOpenReport: () => void;
}> = ({ study, detected, meta, justProcessed, onOpenReport }) => {
  const { token, userId } = useAuth();
  const { data: bbox } = useStudyBbox(token, userId, study.viewerId);
  const predictions = useMemo(() => bbox?.predictions ?? {}, [bbox]);
  const passing = useMemo(() => new Set(detected), [detected]);
  const { findings } = useMemo(
    () => selectGalleryFindings(passing, predictions),
    [passing, predictions],
  );
  const sliceMap = useFindingSlices(study.viewerId, "", findings);
  const tiles = useMemo<Record<string, TileState>>(() => {
    const map: Record<string, TileState> = {};
    for (const f of findings) {
      const s = sliceMap[f.condition];
      const frames = s?.frames ?? [];
      map[f.condition] = {
        imageUrl: frames.length ? frames[Math.floor(frames.length / 2)] : null,
        loading: s?.loading ?? true,
      };
    }
    return map;
  }, [findings, sliceMap]);

  return (
    <UploadDetailPanel
      studyId={study.viewerId}
      findings={findings}
      tileStateByCondition={tiles}
      meta={meta}
      onOpenReport={onOpenReport}
      justProcessed={justProcessed}
    />
  );
};

const UploadStudyPage: React.FC = () => {
  const navigate = useNavigate();
  const { token, userId } = useAuth();
  const {
    batch,
    shareWithCompany: batchShareWithCompany,
    setShareWithCompany: setBatchShareWithCompany,
    ensureReceiptCohortAccess,
  } = useBatchUploadContext();

  // Per-user cohort: self-upload-{sub}. The sub is the token's claim (the backend
  // derives the same), NOT useAuth().userId (which is the backend username).
  const privateCohortId = useMemo(() => {
    const sub = token ? (decodeToken(token) as { sub?: string } | null)?.sub : null;
    return sub ? `self-upload-${sub}` : null;
  }, [token]);

  // company_id claim (minted at login) gates the "share with company" option —
  // when present the user may route the upload into the company-wide cohort.
  const companyId = useMemo(
    () => (token ? (decodeToken(token) as { company_id?: string } | null)?.company_id : null),
    [token],
  );
  const companyCohortId = companyId ? `company-upload-${companyId}` : null;

  const [phase, setPhase] = useState<UploadPhase>("idle");
  const [files, setFiles] = useState<File[]>([]);
  const [validation, setValidation] = useState<StudyValidation | null>(null);
  const [phiFindings, setPhiFindings] = useState<PhiFinding[]>([]);
  const [stripIdentifiers, setStripIdentifiers] = useState(DEFAULT_STRIP_PATIENT_INFO);
  const [acknowledged, setAcknowledged] = useState(false);
  // Destination: private self-upload (default) vs the company-wide shared cohort.
  const [singleShareWithCompany, setSingleShareWithCompany] = useState(false);
  const selectedCohortId = singleShareWithCompany ? companyCohortId : privateCohortId;
  // The backend uses the current profile, not the token's company_id claim, to
  // choose a shared destination. Keep polling the cohort returned in its receipt
  // so a mid-session company reassignment cannot strand this upload in processing.
  const [receiptCohortId, setReceiptCohortId] = useState<string | null>(null);
  const [receiptToken, setReceiptToken] = useState<string | null>(null);
  const cohortId = receiptCohortId ?? selectedCohortId;
  const [progress, setProgress] = useState(0);
  const [processingStage, setProcessingStage] = useState<ProcessingStage>("uploaded");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  // Which pipeline step failed (0=Check/validation, 1=Upload), for the error box.
  const [failedStep, setFailedStep] = useState<number | null>(null);

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [justProcessedId, setJustProcessedId] = useState<string | null>(null);
  const [pendingStudyUid, setPendingStudyUid] = useState<string | null>(null);
  // Failed rows the user dismissed (no server-side delete endpoint yet).
  const [hiddenIds, setHiddenIds] = useState<Set<string>>(new Set());

  // The uploads list AND the post-upload readiness poll are the same query. It
  // refetches while any study is still running, AND throughout the window after
  // a POST (pendingStudyUid set) while we wait for the brand-new cohort to be
  // ingested — the endpoint 404s until reader-ingest lands the first row, so
  // without pollWhilePending the poll would stop on that 404 and never recover.
  const { data: cohort } = useReaderCohort(
    receiptToken ?? token,
    cohortId,
    pendingStudyUid !== null,
  );

  // Batch folder upload (many studies at once). Its state machine lives in the
  // authenticated app shell so navigation does not interrupt upload polling.
  const batchActive = batch.phase !== "idle";

  // Parsed study (bytes + datasets) retained between validate and upload.
  const parsedRef = useRef<ParsedStudy | null>(null);
  // In-flight upload, so cancelling (starting a new upload) aborts the POST
  // rather than leaving an orphaned study to surface later.
  const uploadAbortRef = useRef<AbortController | null>(null);

  const studiesByUid = useMemo(() => {
    const m = new Map<string, ReaderStudyItem>();
    for (const s of cohort?.studies ?? []) m.set(s.study_uid, s);
    return m;
  }, [cohort]);

  const visibleStudies = useMemo(
    () => (cohort?.studies ?? []).filter((s) => !hiddenIds.has(s.study_uid)),
    [cohort, hiddenIds],
  );

  const uploads = useMemo<UploadListItem[]>(
    () => visibleStudies.map(mapToListItem),
    [visibleStudies],
  );

  const [exportingReports, setExportingReports] = useState(false);
  const exportReports = useCallback(async () => {
    if (!token || !userId) return;
    const completed = visibleStudies.filter((study) => study.status === "completed");
    if (!completed.length) return;
    setExportingReports(true);
    try {
      const reports = [];
      // Keep backend report generation bounded: an export can contain up to the
      // full upload worklist, so avoid fanning out hundreds of requests at once.
      for (let index = 0; index < completed.length; index += 4) {
        const batch = completed.slice(index, index + 4);
        const results = await Promise.all(
          batch.map(async (study) => {
            const studyId = study.job_id ? `${study.study_uid}::${study.job_id}` : study.study_uid;
            const [saved, generated] = await Promise.all([
              fetchReportState(token, userId, studyId).catch(() => null),
              fetchReport(studyId, token).catch(() => null),
            ]);
            // "note" keeps the rest of the export downloadable: a report whose
            // Impression cannot be generated states that in its own PDF pages.
            // A case with no report data at all resolves to null and is
            // counted in the shortfall message, never silently dropped.
            return makeDownloadableReport(studyId, saved ?? {}, generated, {
              onImpressionFailure: "note",
            }).catch(() => null);
          }),
        );
        reports.push(...results.filter((report) => report !== null));
      }
      if (!reports.length) {
        window.alert("No completed uploads have report data available yet.");
        return;
      }
      const date = new Date().toISOString().slice(0, 10);
      await downloadCombinedReports(reports, `${cohortId ?? "uploads"}-reports-${date}.pdf`);
      const shortfall = summarizeExportShortfall(completed.length, reports);
      if (shortfall) window.alert(shortfall);
    } catch {
      window.alert("Could not export the reports. No PDF was downloaded.");
    } finally {
      setExportingReports(false);
    }
  }, [cohortId, token, userId, visibleStudies]);

  const startNewUpload = useCallback(() => {
    uploadAbortRef.current?.abort();
    uploadAbortRef.current = null;
    parsedRef.current = null;
    setSelectedId(null);
    setPhase("idle");
    setFiles([]);
    setValidation(null);
    setPhiFindings([]);
    setStripIdentifiers(DEFAULT_STRIP_PATIENT_INFO);
    setAcknowledged(false);
    setProgress(0);
    setErrorMessage(null);
    setFailedStep(null);
    setReceiptCohortId(null);
    setReceiptToken(null);
    batch.reset();
  }, [batch.reset]);

  const handleFilesSelected = useCallback(
    (selected: File[]) => {
      setSelectedId(null);
      setReceiptCohortId(null);
      setReceiptToken(null);
      // Route an oversized single-study selection through the bounded folder
      // state machine. It presents the same review controls, but never retains
      // the complete multi-gigabyte study in memory.
      const selectedBytes = selected.reduce((sum, file) => sum + file.size, 0);
      if (selectedBytes > UPLOAD_SINGLE_REQUEST_MB * 1024 * 1024) {
        batch.begin(selected);
        return;
      }
      setFiles(selected);
      setPhase("parsing");
      setAcknowledged(false);
      setStripIdentifiers(DEFAULT_STRIP_PATIENT_INFO);
      setFailedStep(null);
      // LU-A3/A4/A6: parse + validate + PHI scan + size guard, all client-side.
      void (async () => {
        try {
          const parsed = await parseStudy(selected);
          const maxBytes = UPLOAD_MAX_MB * 1024 * 1024;
          if ((parsed.validation.totalBytes ?? 0) > maxBytes) {
            const mb = Math.round((parsed.validation.totalBytes ?? 0) / (1024 * 1024));
            throw new StudyValidationError(
              `This study is ${mb} MB, over the ${UPLOAD_MAX_MB} MB limit.`,
            );
          }
          parsedRef.current = parsed;
          setValidation(parsed.validation);
          setPhiFindings(parsed.phiFindings);
          setPhase("needs-ack");
        } catch (err) {
          parsedRef.current = null;
          const msg =
            err instanceof StudyValidationError
              ? err.message
              : "Couldn't read these files as a DICOM study.";
          logger.warn(`Live Upload: validation failed — ${msg}`);
          setErrorMessage(msg);
          setFailedStep(0); // Check
          setPhase("error");
        }
      })();
    },
    [batch.begin],
  );

  const handleUpload = useCallback(async () => {
    const parsed = parsedRef.current;
    if (!acknowledged || !parsed) return;
    setPhase("uploading");
    setProgress(0);
    // LU-A5: strip blanks the identifying tags in-place before framing.
    const boundary = makeBoundary();
    const body = buildMultipartBody(parsed.instances, boundary, stripIdentifiers);
    const controller = new AbortController();
    uploadAbortRef.current = controller;
    try {
      const receipt = await uploadStudyDirect({
        studyUid: parsed.studyInstanceUid,
        shared: singleShareWithCompany,
        body,
        contentType: multipartContentType(boundary),
        timeoutMs: UPLOAD_REQUEST_TIMEOUT_MS,
        signal: controller.signal,
        onProgress: (loaded, total) => {
          if (total) setProgress(Math.round((loaded / total) * 100));
        },
      });
      const receiptCohortId = uploadReceiptCohortId(receipt);
      const pollingToken = await ensureReceiptCohortAccess(receiptCohortId);
      setReceiptToken(pollingToken);
      setReceiptCohortId(receiptCohortId);
      // LU-A8: the cohort query polls; the readiness effect below promotes the
      // study to the list + opens it when it lands completed.
      setPendingStudyUid(parsed.studyInstanceUid);
      setProcessingStage("uploaded");
      setPhase("processing");
    } catch (err) {
      // Cancelled by startNewUpload (the user moved on) - not a failure.
      if (isDirectUploadCancelled(err)) return;
      logger.error("Live Upload: study POST failed", err);
      setErrorMessage(uploadErrorMessage(err));
      setFailedStep(1); // Upload
      setPhase("error");
    }
  }, [acknowledged, ensureReceiptCohortAccess, singleShareWithCompany, stripIdentifiers]);

  const handleFolderFiles = useCallback(
    (selected: File[]) => {
      setReceiptCohortId(null);
      setReceiptToken(null);
      batch.begin(selected);
    },
    [batch.begin],
  );

  // LU-A8: watch the polled cohort for the just-uploaded study.
  useEffect(() => {
    if (!pendingStudyUid) return;
    const item = studiesByUid.get(pendingStudyUid);
    if (!item) return; // not propagated to the cohort yet (reader-ingest lag)
    if (item.status === "completed") {
      setProcessingStage("ready");
      setJustProcessedId(pendingStudyUid);
      setSelectedId(pendingStudyUid);
      setPendingStudyUid(null);
      setPhase("idle");
    } else if (item.status === "failed" || item.status === "cancelled") {
      setSelectedId(pendingStudyUid);
      setPendingStudyUid(null);
      setPhase("idle");
    } else {
      setProcessingStage("analyzing");
    }
  }, [studiesByUid, pendingStudyUid]);

  const selectUpload = useCallback((id: string) => setSelectedId(id), []);

  const deleteUpload = useCallback((id: string) => {
    // No server-side delete endpoint yet (TODO) — hide the row client-side.
    setHiddenIds((prev) => new Set(prev).add(id));
    setSelectedId((cur) => (cur === id ? null : cur));
  }, []);

  const handleOpenReport = useCallback(() => {
    // Open the viewer with the compound study_uid::job_id (see UploadListItem.viewerId);
    // the bare study_uid won't resolve a device-server study.
    const target = uploads.find((u) => u.id === selectedId);
    if (target) navigate(`/case-viewer/${target.viewerId}`);
  }, [navigate, selectedId, uploads]);

  // ── Right-pane routing: a selected past upload, else the new-upload flow ──
  const selected = selectedId ? (uploads.find((u) => u.id === selectedId) ?? null) : null;

  let title: string | undefined = "New upload";
  let subtitle: React.ReactNode = "DROP A CT STUDY · PRIVATE TO YOU";
  let contentFills = false;
  let body: React.ReactNode;

  if (selected) {
    const idTail = selected.id.slice(-8);
    if (selected.status === "ready") {
      title = undefined;
      subtitle = undefined;
      contentFills = true; // PreviewGalleryView scrolls internally
      body = (
        <ReadyUploadDetail
          study={selected}
          detected={detectedFindings(studiesByUid.get(selected.id))}
          meta={metaLine(selected).toUpperCase()}
          justProcessed={selected.id === justProcessedId}
          onOpenReport={handleOpenReport}
        />
      );
    } else if (selected.status === "processing") {
      title = "Processing";
      subtitle = (
        <>
          STUDY <span className="text-signal-text-tint">{idTail}</span>
        </>
      );
      body = <ProcessingPipeline stage="analyzing" />;
    } else {
      title = "Upload failed";
      subtitle = (
        <>
          STUDY <span className="text-signal-text-tint">{idTail}</span>
        </>
      );
      body = (
        <ErrorPanel
          message="The study couldn't be processed"
          detail={selected.failureSummary}
          onRetry={() => {
            deleteUpload(selected.id);
            startNewUpload();
          }}
        />
      );
    }
  } else if (batchActive) {
    // Batch folder upload flow — its own state machine (useBatchUpload),
    // rendered below the shared pipeline-box tracker.
    title = batch.phase === "done" ? "Batch complete" : "New batch";
    subtitle =
      batch.phase === "review"
        ? `REVIEW · ${batch.counts.total} STUDIES`
        : batch.phase === "uploading"
          ? "UPLOADING · PARALLEL"
          : batch.phase === "analyzing"
            ? `ANALYZING · ${batch.counts.terminal}/${batch.counts.total}`
            : batch.phase === "done"
              ? "DONE"
              : "READING FOLDER…";
    let batchBody: React.ReactNode;
    if (batch.phase === "parsing") {
      // Grouping the folder into studies — the count isn't known yet, so show a
      // placeholder rather than a misleading "0 studies" that jumps to N.
      batchBody = (
        <div className="flex flex-col items-center justify-center rounded border border-dashed border-border-strong bg-signal-ambient py-16 text-center">
          <div className="font-mono text-[10px] uppercase tracking-[0.08em] text-fg-dim">
            Reading folder
          </div>
          <p className="mt-2 text-[13px] text-fg/55">Grouping DICOM files into studies…</p>
        </div>
      );
    } else if (batch.phase === "review") {
      batchBody = (
        <BatchReviewPanel
          cases={batch.cases}
          excludedStudies={batch.excludedStudies}
          skippedFiles={batch.skippedFiles}
          aggregatePhi={batch.aggregatePhi}
          strip={batch.strip}
          onStripChange={batch.setStrip}
          acknowledged={batch.acknowledged}
          onAcknowledgeChange={batch.setAcknowledged}
          hasCompany={!!companyId}
          shareWithCompany={batchShareWithCompany}
          onShareChange={setBatchShareWithCompany}
          onUpload={batch.startUpload}
          onCancel={startNewUpload}
        />
      );
    } else if (batch.phase === "uploading") {
      batchBody = <BatchUploadPanel cases={batch.cases} />;
    } else if (batch.phase === "analyzing") {
      batchBody = <BatchAnalyzePanel cases={batch.cases} />;
    } else {
      batchBody = (
        <BatchReviewSummary
          cases={batch.cases}
          onRetry={batch.retryCase}
          onOpenCase={(viewerId) => navigate(`/case-viewer/${viewerId}`)}
          onDone={startNewUpload}
        />
      );
    }
    body = (
      <div>
        <BatchProgressStats phase={batch.phase} cases={batch.cases} />
        {batchBody}
      </div>
    );
  } else {
    // The per-phase detail panel — rendered BELOW the persistent pipeline box
    // (except `error`, whose ErrorPanel sits below the box too).
    let phaseBody: React.ReactNode;
    switch (phase) {
      case "idle":
        phaseBody = (
          <UploadModeChooser
            onSingleFiles={handleFilesSelected}
            onFolderFiles={handleFolderFiles}
            maxMb={UPLOAD_MAX_MB}
            // A folder that parses to zero usable studies drops back here — carry
            // the skip/exclude reasons so it isn't a silent "nothing happened".
            skippedFiles={batch.skippedFiles}
            excludedStudies={batch.excludedStudies}
          />
        );
        break;
      case "parsing":
        subtitle = `VALIDATING · ${files.length} FILES`;
        phaseBody = <ParsingPanel fileCount={files.length} validation={validation} />;
        break;
      case "needs-ack":
        subtitle = `REVIEW · ${validation?.modality ?? "CT"}`;
        phaseBody = validation ? (
          <PhiReviewPanel
            validation={validation}
            phiFindings={phiFindings}
            stripIdentifiers={stripIdentifiers}
            onStripChange={setStripIdentifiers}
            acknowledged={acknowledged}
            onAcknowledgeChange={setAcknowledged}
            hasCompany={!!companyId}
            shareWithCompany={singleShareWithCompany}
            onShareChange={setSingleShareWithCompany}
            onUpload={handleUpload}
            onCancel={startNewUpload}
          />
        ) : null;
        break;
      case "uploading":
        subtitle = `${stripIdentifiers ? "STRIPPING + UPLOADING" : "UPLOADING"} · ${validation?.instanceCount ?? files.length} FILES`;
        // Nudge the bar down so it isn't crammed under the box with empty space below.
        phaseBody = (
          <div className="pt-14">
            <UploadProgressPanel
              percent={progress}
              fileCount={validation?.instanceCount ?? files.length}
              bytesUploaded={
                validation?.totalBytes ? (validation.totalBytes * progress) / 100 : undefined
              }
              bytesTotal={validation?.totalBytes}
              stripping={stripIdentifiers}
            />
          </div>
        );
        break;
      case "processing":
        subtitle = "PROCESSING · CT";
        // The box already shows the pipeline — slim ProcessingPipeline to its note.
        phaseBody = <ProcessingPipeline stage={processingStage} showPipeline={false} />;
        break;
      case "error":
        phaseBody = (
          <ErrorPanel message={errorMessage ?? "Upload failed"} onRetry={startNewUpload} />
        );
        break;
      default:
        phaseBody = <DropzonePanel onFiles={handleFilesSelected} maxMb={UPLOAD_MAX_MB} />;
    }

    // Every new-upload phase — including `error` — keeps the persistent pipeline
    // box on top, with the phase's detail panel (or ErrorPanel) beneath it.
    body = (
      <>
        <UploadPipelineBox
          phase={phase}
          maxMb={UPLOAD_MAX_MB}
          stripping={stripIdentifiers}
          failedStep={failedStep ?? undefined}
        />
        <div className="mt-4">{phaseBody}</div>
      </>
    );
  }

  // CSV export is hidden for now: onExport is deliberately not passed.
  // UploadPageView still supports it — re-wire with buildUploadsCsv/downloadCsv
  // (src/utils/uploadsCsv) to restore the button.
  return (
    <UploadPageView
      uploads={uploads}
      selectedId={selectedId}
      onSelectUpload={selectUpload}
      onNewUpload={startNewUpload}
      onDeleteUpload={deleteUpload}
      newUploadActive={!selected && !batchActive}
      batch={batchActive ? { phase: batch.phase, cases: batch.cases } : undefined}
      batchSelected={batchActive && !selected}
      onSelectBatch={() => setSelectedId(null)}
      onExportReports={exportReports}
      exportingReports={exportingReports}
      title={title}
      subtitle={subtitle}
      contentFills={contentFills}
    >
      {body}
    </UploadPageView>
  );
};

export default UploadStudyPage;
